import type { EditionFixture } from "../lib/adapter";
import candidates from "./published/candidates.json";
import rundown from "./published/rundown.json";
import script from "./published/script.json";
import verdict from "./published/verdict.json";
import synthesis from "./published/synthesis.json";
import publish from "./published/publish.json";

export const publishedFixture: EditionFixture = {
  candidates: candidates as never,
  rundown: rundown as never,
  script: script as never,
  verdict: verdict as never,
  synthesis: synthesis as never,
  publish: publish as never,
  audio_url: "https://audio.overnightnewsroom.in/episodes/2026-07-12-1300.mp3",
  timeline: [
    {
      label: "Monitor",
      name: "MONITOR",
      timestamp: "2026-07-12T07:30:02Z",
      duration_seconds: 7.4,
      status: "done",
      detail: "24 candidates from 3 feeds · 3 healthy feeds",
    },
    {
      label: "Editor",
      name: "EDITOR",
      timestamp: "2026-07-12T07:31:00Z",
      duration_seconds: 21.2,
      status: "done",
      detail: "3 selected · 1 excluded (stale) · target 120s",
    },
    {
      label: "Writer",
      name: "WRITER",
      timestamp: "2026-07-12T07:32:00Z",
      duration_seconds: 28.9,
      status: "done",
      detail: "6 lines · 3 factual claims · 2 anchor voices",
    },
    {
      label: "Judge",
      name: "JUDGE",
      timestamp: "2026-07-12T07:33:00Z",
      duration_seconds: 35.1,
      status: "done",
      detail: "3 pass / 0 block · gate recommendation: PASS",
    },
    {
      label: "Publisher",
      name: "PUBLISHER",
      timestamp: "2026-07-12T07:35:00Z",
      duration_seconds: 18.6,
      status: "done",
      detail: "Telegram + R2 · both succeeded",
    },
  ],
  blocked_examples: [
    {
      line_id: "L05-draft",
      reason: "Draft line contained a casualty figure not present in any source record.",
      action: "Line removed during Writer repair pass; no audio segment generated.",
    },
  ],
  previous_editions: [
    {
      episode_slug: "2026-07-12-0900",
      title: "सुबह की स्रोत-जाँची खबरें",
      published_at: "2026-07-12T03:35:00Z",
      duration_seconds: 105,
      story_count: 3,
      source_count: 3,
      claim_counts: { pass: 3, rewrite: 0, block: 1 },
      audio_url: "https://audio.overnightnewsroom.in/episodes/2026-07-12-0900.mp3",
    },
    {
      episode_slug: "2026-07-11-1700",
      title: "शाम की स्रोत-जाँची खबरें",
      published_at: "2026-07-11T11:35:00Z",
      duration_seconds: 112,
      story_count: 4,
      source_count: 4,
      claim_counts: { pass: 4, rewrite: 0, block: 0 },
      audio_url: "https://audio.overnightnewsroom.in/episodes/2026-07-11-1700.mp3",
    },
  ],
  correction_note: null,
  corrected_at: null,
  next_scheduled: "2026-07-12T11:30:00Z",
  blocked_reason: null,
  run_state_override: null,
};

export const blockedFixture: EditionFixture = {
  ...publishedFixture,
  run_state_override: "BLOCKED",
  blocked_reason: "Source gate did not pass — 1 claim could not be verified against available sources.",
  audio_url: null,
  timeline: [
    {
      label: "Monitor",
      name: "MONITOR",
      timestamp: "2026-07-12T07:30:02Z",
      duration_seconds: 7.4,
      status: "done",
      detail: "24 candidates from 3 feeds",
    },
    {
      label: "Editor",
      name: "EDITOR",
      timestamp: "2026-07-12T07:31:00Z",
      duration_seconds: 21.2,
      status: "done",
      detail: "3 selected · 1 excluded",
    },
    {
      label: "Writer",
      name: "WRITER",
      timestamp: "2026-07-12T07:32:00Z",
      duration_seconds: 28.9,
      status: "done",
      detail: "6 lines · 3 factual claims",
    },
    {
      label: "Judge",
      name: "JUDGE",
      timestamp: "2026-07-12T07:33:00Z",
      duration_seconds: 35.1,
      status: "done",
      detail: "2 pass / 1 block · gate recommendation: BLOCK",
    },
    {
      label: "Publisher",
      name: "PUBLISHER",
      timestamp: "",
      duration_seconds: 0,
      status: "blocked",
      detail: "Edition withheld — source gate did not pass",
    },
  ],
  blocked_examples: [
    {
      line_id: "L05-draft",
      reason: "Draft line contained a casualty figure not present in any source record.",
      action: "Line removed; edition blocked after repair attempt failed re-judge.",
    },
  ],
  previous_editions: publishedFixture.previous_editions,
};

export const partialPublishFixture: EditionFixture = {
  ...publishedFixture,
  run_state_override: "PUBLISH_PARTIAL",
  publish: {
    ...publishedFixture.publish,
    destinations: [
      {
        destination: "telegram" as const,
        state: "FAILED" as const,
        idempotency_key: "2026-07-12-1300-Asia-Kolkata:telegram",
        external_id: null,
        external_url: null,
        error: "Telegram API timeout after 3 retries",
      },
      {
        destination: "r2" as const,
        state: "SUCCEEDED" as const,
        idempotency_key: "2026-07-12-1300-Asia-Kolkata:r2",
        external_id: "episodes/2026-07-12-1300.mp3",
        external_url: "https://audio.overnightnewsroom.in/episodes/2026-07-12-1300.mp3",
        error: null,
      },
    ],
  },
  timeline: [
    ...publishedFixture.timeline.slice(0, 4),
    {
      label: "Publisher",
      name: "PUBLISHER",
      timestamp: "2026-07-12T07:35:00Z",
      duration_seconds: 18.6,
      status: "done",
      detail: "R2 succeeded · Telegram failed (timeout) — partial publish",
    },
  ],
};

export const noEditionFixture: EditionFixture = {
  ...publishedFixture,
  run_state_override: "NO_EDITION",
  audio_url: null,
  next_scheduled: "2026-07-12T07:30:00Z",
  timeline: [
    {
      label: "Monitor",
      name: "MONITOR",
      timestamp: "2026-07-12T07:30:02Z",
      duration_seconds: 7.4,
      status: "done",
      detail: "0 fresh candidates — feeds returned only stale items",
    },
    {
      label: "Editor",
      name: "EDITOR",
      timestamp: "",
      duration_seconds: 0,
      status: "skipped",
      detail: "No candidates to evaluate",
    },
    {
      label: "Writer",
      name: "WRITER",
      timestamp: "",
      duration_seconds: 0,
      status: "skipped",
      detail: "Skipped — no stories selected",
    },
    {
      label: "Judge",
      name: "JUDGE",
      timestamp: "",
      duration_seconds: 0,
      status: "skipped",
      detail: "Skipped — no script produced",
    },
    {
      label: "Publisher",
      name: "PUBLISHER",
      timestamp: "",
      duration_seconds: 0,
      status: "skipped",
      detail: "No edition to publish",
    },
  ],
  blocked_examples: [],
  previous_editions: publishedFixture.previous_editions,
};

export const correctedFixture: EditionFixture = {
  ...publishedFixture,
  correction_note: "A source attribution in line L003 was corrected from 'Indian Express' to 'The Indian Express' after publication.",
  corrected_at: "2026-07-12T08:15:00Z",
  previous_editions: [
    {
      episode_slug: "2026-07-12-1300-original",
      title: "दोपहर की स्रोत-जाँची खबरें (original)",
      published_at: "2026-07-12T07:35:00Z",
      duration_seconds: 90,
      story_count: 3,
      source_count: 3,
      claim_counts: { pass: 3, rewrite: 0, block: 0 },
      audio_url: "https://audio.overnightnewsroom.in/episodes/2026-07-12-1300-original.mp3",
    },
    ...publishedFixture.previous_editions,
  ],
};

export const loadingFixture = null;

export type FixtureName = "published" | "blocked" | "partial" | "no_edition" | "corrected" | "loading";

export const fixtureMap: Record<FixtureName, EditionFixture | null> = {
  published: publishedFixture,
  blocked: blockedFixture,
  partial: partialPublishFixture,
  no_edition: noEditionFixture,
  corrected: correctedFixture,
  loading: loadingFixture,
};
